// Central editable content for Primevanta Global.
// All business-specific values are placeholders and clearly editable.

export const BUSINESS = {
  name: "Primevanta Global",
  descriptor: "Documentation & Business Support",
  tagline: "Accuracy Beyond Words",
  founded: "2004",
  city: "New Delhi, India",
  founder: "Maniesh Srivastva",
  founderPortrait: "/founder.png",
  phone: "+91-95376-07166",
  whatsapp: "+91-95376-07166",
  email: "manieshsrivastva@gmail.com",
  address: "396, First Floor, Pocket 16, Sector 3, Dwarka, New Delhi, India 110078",
  hours: "Mon–Sat: 9:15 am – 5:45 pm · Sun: Closed",
  hoursList: [
    { day: "Monday", time: "9:15 am – 5:45 pm" },
    { day: "Tuesday", time: "9:15 am – 5:45 pm" },
    { day: "Wednesday", time: "9:15 am – 5:45 pm" },
    { day: "Thursday", time: "9:15 am – 5:45 pm" },
    { day: "Friday", time: "9:15 am – 5:45 pm" },
    { day: "Saturday", time: "9:15 am – 5:45 pm" },
    { day: "Sunday", time: "Closed" },
  ],
  mapsUrl: "https://maps.google.com/?q=396+Pocket+16+Sector+3+Dwarka+New+Delhi+110078",
  linkedinProfile: "https://www.linkedin.com/in/manieshsrivastva",
  linkedinCompany: "https://www.linkedin.com/in/manieshsrivastva",
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Quality & Process", to: "/quality-process" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export const SERVICE_GROUPS = [
  {
    group: "Healthcare Documentation",
    items: [
      { title: "Clinical Documentation", slug: "clinical-documentation" },
      { title: "Medical Transcription", slug: "medical-transcription" },
      { title: "Medical Scribing", slug: "medical-scribing" },
      { title: "EMR/EHR Documentation Support", slug: "emr-ehr-support" },
      { title: "Editing & Quality Assurance", slug: "editing-quality-assurance" },
      { title: "AI-Assisted Draft Review", slug: "ai-content-humanisation" },
    ],
  },
  {
    group: "Healthcare Back-Office Support",
    items: [
      { title: "Medical Billing Support", slug: "medical-billing-coding" },
      { title: "Medical Coding Support", slug: "medical-billing-coding" },
      { title: "Process Coordination", slug: "medical-billing-coding" },
      { title: "Data-Entry Support", slug: "typing-data-entry" },
    ],
  },
  {
    group: "Professional Transcription",
    items: [
      { title: "Medico-Legal Transcription", slug: "medico-legal-transcription" },
      { title: "Legal Transcription", slug: "legal-transcription" },
      { title: "Academic & Research", slug: "academic-research" },
      { title: "Business Transcription", slug: "business-transcription" },
    ],
  },
  {
    group: "Editing & Content",
    items: [
      { title: "Medical Editing", slug: "editing-quality-assurance" },
      { title: "Proofreading", slug: "editing-quality-assurance" },
      { title: "Business Writing", slug: "business-transcription" },
      { title: "Book & eBook Editing", slug: "academic-research" },
      { title: "AI-Content Humanisation", slug: "ai-content-humanisation" },
    ],
  },
  {
    group: "Typing & Data",
    items: [
      { title: "English & Hindi Typing", slug: "typing-data-entry" },
      { title: "Handwritten-to-Digital", slug: "typing-data-entry" },
      { title: "PDF-to-Editable Conversion", slug: "typing-data-entry" },
      { title: "Data Entry & Formatting", slug: "typing-data-entry" },
    ],
  },
];

// Individual service page content
export const SERVICES = {
  "clinical-documentation": {
    title: "Clinical Documentation",
    eyebrow: "Healthcare Documentation",
    lead: "Accurate, structured support for clinical records, dictated notes and healthcare documentation workflows.",
    includes: ["Structured clinical note preparation", "Dictation and source-file transcription", "Template and specialty formatting", "Terminology and consistency review", "Human quality check before delivery"],
    forWho: ["Physicians and specialists", "Clinics and group practices", "Hospitals and departments", "Documentation outsourcing partners"],
    useCases: ["Progress notes and consultation records", "Discharge and referral documentation", "Specialty-specific structured records"],
    inputs: ["Audio or dictation files (securely transferred)", "Preferred templates and formatting rules", "Specialty and terminology preferences"],
    workflow: ["Scope confirmation", "Controlled assignment", "Transcription & structuring", "Terminology & QA review", "Delivery & feedback"],
    quality: "Documentation is reviewed for terminology, context, structure and consistency by an experienced human reviewer before delivery.",
  },
  "medical-transcription": {
    title: "Medical Transcription",
    eyebrow: "Healthcare Documentation",
    lead: "Human-led transcription adapted to specialty, template and workflow requirements.",
    includes: ["Audio-to-text transcription", "Specialty terminology handling", "Template-based formatting", "Editing and proofreading", "Final human quality review"],
    forWho: ["Physicians and clinics", "Hospitals", "Medical transcription outsourcing partners"],
    useCases: ["Dictated clinical notes", "Consultation and procedure records", "Overflow transcription support"],
    inputs: ["Dictation / audio files", "Formatting templates", "Turnaround expectations"],
    workflow: ["Scope confirmation", "Assignment", "Transcription", "Editing & QA", "Delivery"],
    quality: "Every file passes a human review focused on medical terminology, meaning and formatting consistency.",
  },
  "medical-scribing": {
    title: "Medical Scribing",
    eyebrow: "Healthcare Documentation",
    lead: "Scribing assistance that supports clinicians with real-time or asynchronous documentation.",
    includes: ["Encounter documentation support", "Structured note preparation", "Template adherence", "Terminology attention", "Human review of output"],
    forWho: ["Individual clinicians", "Specialty practices", "Care teams"],
    useCases: ["Encounter note support", "Reducing clinician documentation burden"],
    inputs: ["Workflow and template details", "Access arrangements (confirmed securely)"],
    workflow: ["Requirement discussion", "Workflow setup", "Documentation support", "Review", "Delivery"],
    quality: "Scribing output is verified by a human reviewer for accuracy and completeness.",
  },
  "emr-ehr-support": {
    title: "EMR/EHR Documentation Support",
    eyebrow: "Healthcare Documentation",
    lead: "Flexible documentation support structured around your EMR/EHR workflow.",
    includes: ["Structured data preparation", "Template-aligned documentation", "Consistency and formatting review", "Coordination with your team"],
    forWho: ["Practices using EMR/EHR systems", "Hospitals", "Outsourcing partners"],
    useCases: ["Documentation entry support", "Backlog and overflow assistance"],
    inputs: ["System workflow details (confirmed)", "Templates and rules"],
    workflow: ["Scope confirmation", "Workflow setup", "Documentation support", "QA", "Delivery"],
    quality: "Work is aligned to your confirmed workflow and reviewed by a human before hand-off.",
  },
  "editing-quality-assurance": {
    title: "Editing & Quality Assurance",
    eyebrow: "Editing & Content",
    lead: "Detailed review of terminology, context, grammar, formatting, consistency and documentation quality.",
    includes: ["Terminology and context review", "Grammar and clarity editing", "Formatting and consistency checks", "Structured feedback", "Final quality verification"],
    forWho: ["Healthcare and business clients", "Publishers and authors", "Documentation teams"],
    useCases: ["High-volume document QA", "Editing dictated or drafted content", "Standardising formatting across documents"],
    inputs: ["Source documents", "Style or formatting guidelines"],
    workflow: ["Scope confirmation", "Assignment", "Editing", "Quality checks", "Delivery & feedback"],
    quality: "Editing is performed and verified by experienced human reviewers, not automated tools alone.",
  },
  "ai-content-humanisation": {
    title: "AI-Assisted Content Editing & Humanisation",
    eyebrow: "Editing & Content",
    lead: "Human editing that improves accuracy, clarity, tone and natural flow in AI-assisted medical and business content.",
    includes: ["Context-aware review", "Terminology attention", "Tone and meaning preservation", "Formatting consistency", "Escalation of uncertain content", "Final human quality check"],
    forWho: ["Teams using AI or speech-recognition drafts", "Healthcare and business content owners"],
    useCases: ["Refining AI-generated drafts", "Reviewing speech-recognition output", "Humanising machine-assisted content"],
    inputs: ["AI-assisted or raw drafts", "Intended tone and audience"],
    workflow: ["Scope confirmation", "Assignment", "Human review & edit", "Quality check", "Delivery"],
    quality: "Automated output is reviewed by a human to resolve terminology, context, omissions and factual inconsistencies.",
  },
  "medical-billing-coding": {
    title: "Medical Billing & Coding Support",
    eyebrow: "Back-Office Support",
    lead: "Flexible back-office support for confirmed billing and coding processes, designed around client workflows.",
    includes: ["Process-aligned billing support", "Coding support within confirmed scope", "Data preparation and coordination", "Consistency and accuracy review"],
    forWho: ["Medical billing companies", "Practices and clinics", "Outsourcing partners"],
    useCases: ["Overflow billing support", "Coordinated back-office assistance"],
    inputs: ["Confirmed process details", "Required formats and rules"],
    workflow: ["Scope confirmation", "Workflow setup", "Support delivery", "Review", "Delivery"],
    quality: "Support is structured strictly around your confirmed processes and reviewed by a human. Exact sub-services are subject to capability confirmation.",
  },
  "medico-legal-transcription": {
    title: "Medico-Legal Transcription",
    eyebrow: "Professional Transcription",
    lead: "Careful transcription for medico-legal documentation with attention to accuracy and formatting.",
    includes: ["Audio-to-text transcription", "Terminology attention", "Structured formatting", "Human review"],
    forWho: ["Medico-legal professionals", "Law firms", "Healthcare-legal teams"],
    useCases: ["Medico-legal reports", "Recorded statements and reviews"],
    inputs: ["Audio files (securely transferred)", "Formatting requirements"],
    workflow: ["Scope confirmation", "Assignment", "Transcription", "QA", "Delivery"],
    quality: "Content is reviewed by a human for accuracy and consistency before delivery.",
  },
  "legal-transcription": {
    title: "Legal Transcription",
    eyebrow: "Professional Transcription",
    lead: "Structured legal transcription with attention to formatting and terminology.",
    includes: ["Audio-to-text transcription", "Legal terminology attention", "Formatting to requirements", "Human review"],
    forWho: ["Law firms", "Legal professionals", "Consultants"],
    useCases: ["Recorded proceedings and meetings", "Dictated legal documents"],
    inputs: ["Audio files", "Formatting and style rules"],
    workflow: ["Scope confirmation", "Assignment", "Transcription", "QA", "Delivery"],
    quality: "Human review ensures accuracy, terminology and formatting consistency.",
  },
  "academic-research": {
    title: "Academic & Research Support",
    eyebrow: "Professional Transcription",
    lead: "Transcription and editing support for researchers, universities, authors and publishers.",
    includes: ["Interview and research transcription", "Editing and proofreading", "Formatting to guidelines", "Book and eBook editing support"],
    forWho: ["Universities and researchers", "Authors and publishers"],
    useCases: ["Research interviews", "Manuscript editing", "Academic documentation"],
    inputs: ["Audio or documents", "Style and formatting guidelines"],
    workflow: ["Scope confirmation", "Assignment", "Transcription/editing", "QA", "Delivery"],
    quality: "Work is reviewed by a human for accuracy, clarity and adherence to guidelines.",
  },
  "business-transcription": {
    title: "Business Transcription & Documentation",
    eyebrow: "Professional Transcription",
    lead: "Transcription, business writing and documentation support for organisations.",
    includes: ["Meeting and interview transcription", "Business writing and editing", "Formatting and structuring", "Human quality review"],
    forWho: ["Businesses and consultants", "Teams needing documentation support"],
    useCases: ["Meeting records", "Business documents and reports"],
    inputs: ["Audio or drafts", "Formatting preferences"],
    workflow: ["Scope confirmation", "Assignment", "Work & editing", "QA", "Delivery"],
    quality: "All output is reviewed by a human before delivery.",
  },
  "typing-data-entry": {
    title: "Typing & Data Entry",
    eyebrow: "Typing & Data",
    lead: "English and Hindi typing, document conversion and data-entry support.",
    includes: ["English & Hindi typing", "Handwritten-to-digital conversion", "PDF-to-editable conversion", "Data entry and formatting"],
    forWho: ["Businesses", "Professionals", "Publishers and authors"],
    useCases: ["Document digitisation", "Data entry projects", "Format conversion"],
    inputs: ["Source files or documents", "Format requirements"],
    workflow: ["Scope confirmation", "Assignment", "Typing/entry", "QA", "Delivery"],
    quality: "Work is checked by a human for accuracy and formatting consistency.",
  },
};

export const INDUSTRIES = [
  { title: "Healthcare & Clinical Practices", desc: "Documentation support for physicians, specialists and clinics.", icon: "Stethoscope" },
  { title: "Hospitals & Healthcare Organisations", desc: "Structured support for high-volume documentation workflows.", icon: "Building2" },
  { title: "Medical Billing & Outsourcing Partners", desc: "Flexible back-office and overflow support.", icon: "Network" },
  { title: "Law Firms & Medico-Legal Professionals", desc: "Careful legal and medico-legal transcription.", icon: "Scale" },
  { title: "Universities & Researchers", desc: "Research transcription and academic editing.", icon: "GraduationCap" },
  { title: "Publishers, Authors & Businesses", desc: "Editing, business writing and documentation.", icon: "BookOpen" },
];

export const PROCESS = [
  { n: "01", title: "Tell us what you need", text: "Share the service, volume, timeline and required output. Do not submit confidential records through the general form." },
  { n: "02", title: "We review the scope", text: "Primevanta confirms capability, workflow, turnaround and commercial terms." },
  { n: "03", title: "Complete a sample or pilot", text: "Where appropriate, begin with a controlled, non-sensitive or securely transferred sample." },
  { n: "04", title: "Set up the workflow", text: "Confirm templates, file transfer, quality rules, communication and escalation." },
  { n: "05", title: "Deliver and review", text: "Receive the completed work and provide structured feedback." },
];

export const WHY = [
  { title: "Healthcare-First Foundation", text: "The business has been built around medical documentation and complex terminology." },
  { title: "Human Quality Control", text: "Workflows can combine technology-assisted efficiency with human verification." },
  { title: "Flexible Engagement", text: "Support may be structured for individual assignments, recurring projects, overflow work or dedicated teams." },
  { title: "Multi-Domain Documentation", text: "Healthcare specialisation is complemented by legal, academic and business capability." },
  { title: "Direct Communication", text: "A founder-led structure supports accountable and practical engagement." },
  { title: "Process-Led Delivery", text: "Projects are planned around scope, format, turnaround, review and delivery requirements." },
];

export const FAQS = [
  { q: "What services does Primevanta Global provide?", a: "Human-led clinical documentation, medical transcription and scribing, editing and quality assurance, AI-assisted content review, medical billing and coding support, and professional transcription for legal, academic and business clients, along with typing and data entry.", cat: "General" },
  { q: "Do you work only with healthcare clients?", a: "No. Healthcare documentation is our foundation, but we also support legal, medico-legal, academic, research and business workflows.", cat: "General" },
  { q: "Can you review AI-generated or speech-recognition content?", a: "Yes. We provide experienced human review to improve accuracy, terminology, tone and formatting in AI-assisted drafts.", cat: "Editing and AI-assisted content" },
  { q: "Do you provide medical billing and coding support?", a: "We offer flexible back-office support structured around your confirmed processes. Exact sub-services are subject to capability confirmation.", cat: "Billing and coding support" },
  { q: "Can we begin with a sample?", a: "Where appropriate, we can start with a controlled, non-sensitive or securely transferred sample or pilot.", cat: "Samples and pilots" },
  { q: "How do you protect confidential information?", a: "We work within controlled workflows and discuss secure transfer arrangements after an initial review. Please do not send confidential records through the general enquiry form.", cat: "Confidentiality" },
  { q: "What is your turnaround time?", a: "Turnaround depends on scope, volume and complexity and is confirmed during scoping. We do not publish guaranteed turnaround times.", cat: "Pricing and turnaround" },
  { q: "How is pricing calculated?", a: "Pricing is based on the service, volume, complexity and turnaround requirements, and is confirmed after we review your needs.", cat: "Pricing and turnaround" },
  { q: "How do we transfer files securely?", a: "Secure transfer arrangements are discussed after the initial project review. The general enquiry form should not be used for sensitive files.", cat: "File transfer" },
  { q: "Do you support international projects?", a: "Yes. We are based in New Delhi and support remote projects across India and international markets.", cat: "International projects" },
];

export const CASE_STUDIES = [
  { title: "Clinical Documentation Workflow Support", tag: "Healthcare" },
  { title: "Editing and QA for High-Volume Documents", tag: "Editing & QA" },
  { title: "Offshore Documentation Support", tag: "Outsourcing" },
];

export const INSIGHTS = [
  { slug: "human-quality-layer", title: "Why documentation still needs a human quality layer", cat: "Quality", read: "5 min", excerpt: "AI can draft quickly, but terminology, context and accountability still require experienced human judgement." },
  { slug: "reviewing-ai-drafts", title: "A practical approach to reviewing AI-assisted drafts", cat: "AI Review", read: "6 min", excerpt: "How structured human review resolves omissions, tone and factual inconsistencies in machine-assisted content." },
  { slug: "scoping-documentation", title: "Scoping a documentation project the right way", cat: "Process", read: "4 min", excerpt: "Clear instructions, templates and quality rules are the foundation of dependable delivery." },
];
